import type { PdpEddProductSpecs } from '../domain/models/ResolvedExpectedDeliveryDate';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function numberSuffix(i: number): string {
  const j = i % 10;
  const k = i % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

export function isTDD(date: string | Date): boolean {
  const edd = new Date(date);
  const today = new Date();
  return (
    new Date(edd.getFullYear(), edd.getMonth(), edd.getDate()).getTime() ===
    new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
  );
}

export function isNDDDate(date: string | Date): boolean {
  const edd = new Date(date);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  return (
    new Date(edd.getFullYear(), edd.getMonth(), edd.getDate()).getTime() ===
    new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate()).getTime()
  );
}

export function formatEddDate(fullDate: string): string {
  const date = new Date(fullDate);
  const dayString = DAY_NAMES[date.getDay()];
  const month = MONTH_NAMES[date.getMonth()];
  const day = date.getDate();

  if (isTDD(fullDate)) return `Today Evening (${day}${numberSuffix(day)} ${month})`;
  if (isNDDDate(fullDate)) return `Tomorrow (${day}${numberSuffix(day)} ${month})`;
  return `${dayString}, ${month} ${day}`;
}

export function isSolitaireProduct(
  solitaireCarat: number | null,
  solitaireClarity: string | null,
  solitaireColor: string | null
): boolean {
  return Boolean(solitaireCarat && solitaireClarity && solitaireColor);
}

export function getCustomizationEddKey(
  metal: string,
  goldPurity: string | null,
  diamondStoneType: string | null,
  solitaireCarat: number | null,
  solitaireClarity: string | null,
  solitaireColor: string | null,
  isSolitaire: boolean
): string {
  let normalizedMetal = metal;
  if (metal === 'Gold') normalizedMetal = 'Yellow';
  else if (metal === 'Platinum') normalizedMetal = '950';
  else normalizedMetal = metal.split(' ')[0];

  const stoneType = !diamondStoneType || isSolitaire ? '' : diamondStoneType;
  const purity: string | number = goldPurity ? parseInt(goldPurity, 10) : '';
  const carat = solitaireCarat != null ? solitaireCarat.toFixed(2) : '';
  const clarity = solitaireClarity ?? '';
  const color = solitaireColor ?? '';

  return `${normalizedMetal}${purity}${stoneType}${carat}${clarity}${color}`.toUpperCase();
}

export function getCustomizationEddKeyFromSpecs(productSpecs: PdpEddProductSpecs): string {
  const isSolitaire = isSolitaireProduct(
    productSpecs.solitaireCarat,
    productSpecs.solitaireClarity,
    productSpecs.solitaireColor
  );
  return getCustomizationEddKey(
    productSpecs.metal,
    productSpecs.goldPurity,
    productSpecs.diamondStoneType,
    productSpecs.solitaireCarat,
    productSpecs.solitaireClarity,
    productSpecs.solitaireColor,
    isSolitaire
  );
}

export function isSizeableSkuMap(skuMap: Record<string, unknown>): boolean {
  const keys = Object.keys(skuMap);
  if (keys.length > 0) return keys[0] !== '00';
  return false;
}

export function isEarlyDeliverable(date: string | null, deliveryPeriod: number): boolean {
  if (!date) {
    // Matches old-react eddUtil.isEarlyDeliverable(null): epoch date is treated as soon deliverable.
    return true;
  }

  const today = new Date();
  const deliveryDate = new Date(date);
  if (Number.isNaN(deliveryDate.getTime())) {
    return true;
  }

  return Math.round((deliveryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) <= deliveryPeriod;
}

export function getDeliveryPeriodEnd(periodDays: number): Date {
  const end = new Date();
  end.setDate(end.getDate() + periodDays);
  return end;
}

export function getSameDayMessage(fullDate: string): string | null {
  const today = new Date();
  const dateStr = `${today.getFullYear()}/${today.getMonth()}/${today.getDate()}`;
  const timeStr = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
  const shouldShow =
    Date.parse(`${dateStr} ${timeStr}`) < Date.parse(`${dateStr} 16:00:00`);

  if (!shouldShow) return null;
  if (isTDD(fullDate)) return 'same-day-delivery';
  if (isNDDDate(fullDate)) return 'next-day-delivery';
  return null;
}
